
import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2, X } from 'lucide-react';

import { academicApi } from '../../../academics/services/academicApi';
import { useSchool } from '../../../../context/SchoolContext';
import { getStaffApiErrorMessage } from '../../utils/staffErrors';

const today = () => new Date().toISOString().slice(0, 10);

const createInitialFormData = () => ({
  academicYear: '',
  classLevel: '',
  section: '',
  assignedDate: today(),
  notes: '',
  status: 'ACTIVE',
});

const fieldClass = (error, disabled = false) =>
  `w-full rounded-xl border px-4 py-3 text-sm outline-none transition ${
    error
      ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
      : disabled
        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
  }`;

const AddClassTeacherAssignmentModal = ({
  isOpen,
  onSubmit,
  onClose,
  loading,
  schoolId,
}) => {
  const { activeAcademicYear, activeAcademicYearId } = useSchool();
  const [formData, setFormData] = useState(createInitialFormData);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState('');

  useEffect(() => {
    if (!isOpen || !schoolId) return;

    const initial = {
      ...createInitialFormData(),
      academicYear: activeAcademicYearId || '',
    };
    setFormData(initial);
    setClasses([]);
    setSections([]);
    setErrors({});
    setRequestError('');

    if (!activeAcademicYearId) return;

    setLoadingClasses(true);
    academicApi
      .getClassLevels(schoolId, {
        academicYearId: activeAcademicYearId,
        status: 'ACTIVE',
      })
      .then(setClasses)
      .catch((error) =>
        setRequestError(
          getStaffApiErrorMessage(error, 'Unable to load classes.'),
        ),
      )
      .finally(() => setLoadingClasses(false));
  }, [activeAcademicYearId, isOpen, schoolId]);

  const handleClassChange = async (event) => {
    const classLevel = event.target.value;
    setFormData((current) => ({
      ...current,
      classLevel,
      section: '',
    }));
    setErrors((current) => ({
      ...current,
      classLevel: '',
      section: '',
    }));
    setSections([]);

    if (!classLevel) return;

    setLoadingSections(true);
    try {
      const data = await academicApi.getSections(schoolId, {
        academicYearId: formData.academicYear,
        classLevelId: classLevel,
        status: 'ACTIVE',
      });
      setSections(data);
    } catch (error) {
      setRequestError(
        getStaffApiErrorMessage(error, 'Unable to load sections.'),
      );
    } finally {
      setLoadingSections(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!formData.academicYear) nextErrors.academicYear = 'Academic year is required.';
    if (!formData.classLevel) nextErrors.classLevel = 'Class is required.';
    if (!formData.section) nextErrors.section = 'Section is required.';
    if (!formData.assignedDate) nextErrors.assignedDate = 'Assigned date is required.';
    if (!formData.status) nextErrors.status = 'Status is required.';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setRequestError('');
    try {
      await onSubmit(formData);
    } catch (error) {
      setRequestError(
        getStaffApiErrorMessage(
          error,
          'Unable to create class teacher assignment.',
        ),
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-6 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-[620px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Add Class Teacher Assignment
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Academic year follows the global working context. Select class and section.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {requestError && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {requestError}
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Working Academic Year
            </label>
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
              {activeAcademicYear?.year_name || 'No academic year selected'}
            </div>
            {errors.academicYear && <p className="mt-1 text-xs text-red-600">{errors.academicYear}</p>}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Class <span className="text-red-500">*</span>
            </label>
            <select
              name="classLevel"
              value={formData.classLevel}
              onChange={handleClassChange}
              disabled={!formData.academicYear || loadingClasses}
              className={fieldClass(errors.classLevel, !formData.academicYear || loadingClasses)}
            >
              <option value="">
                {loadingClasses
                  ? 'Loading classes...'
                  : formData.academicYear
                    ? 'Select class'
                    : 'Select a working academic year in the sidebar'}
              </option>
              {classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.class_name || item.name}
                </option>
              ))}
            </select>
            {errors.classLevel && <p className="mt-1 text-xs text-red-600">{errors.classLevel}</p>}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Section <span className="text-red-500">*</span>
            </label>
            <select
              name="section"
              value={formData.section}
              onChange={handleChange}
              disabled={!formData.classLevel || loadingSections}
              className={fieldClass(errors.section, !formData.classLevel || loadingSections)}
            >
              <option value="">
                {loadingSections
                  ? 'Loading sections...'
                  : formData.classLevel
                    ? 'Select section'
                    : 'Select class first'}
              </option>
              {sections.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.section_name || item.name}
                </option>
              ))}
            </select>
            {errors.section && <p className="mt-1 text-xs text-red-600">{errors.section}</p>}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Assigned Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="assignedDate"
              value={formData.assignedDate}
              onChange={handleChange}
              className={fieldClass(errors.assignedDate)}
            />
            {errors.assignedDate && <p className="mt-1 text-xs text-red-600">{errors.assignedDate}</p>}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Notes <span className="font-normal text-gray-400">(Optional)</span>
            </label>
            <textarea
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full resize-none rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={fieldClass(errors.status)}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Saving...' : 'Save Assignment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClassTeacherAssignmentModal;
